import { zodResolver } from '@hookform/resolvers/zod';
import {
  CreateMarketingCostHeadRequestSchema,
  type CreateMarketingCostHeadRequest,
  type MarketingCostHead,
} from '@utazzo/shared';
import {
  UzButton,
  UzDrawer,
  UzForm,
  UzFormField,
  UzInput,
  UzNumberInput,
  UzSelect,
  UzSwitch,
  UzTextarea,
} from '@utazzo/ui';
import React, { useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { useCreateCostHead, useUpdateCostHead } from '../use-marketing-reports';

import { deriveCode, useAutoDeriveCode } from '@/components/master-code-field';
import { useQuerySourceOptions } from '@/features/query-sources/use-query-sources';

interface CostHeadFormDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  head?: MarketingCostHead | null;
}

const CREATE_DEFAULTS: CreateMarketingCostHeadRequest = {
  code: '',
  name: '',
  description: '',
  mode: 'PER_MONTH',
  driver: null,
  rate: null,
  querySourceId: null,
  isRequired: false,
  isActive: true,
  sortOrder: 0,
};

const MODE_OPTIONS = [
  {
    value: 'PER_MONTH',
    label: 'Entered per month',
    description: 'A bill that changes month to month - type it for each month.',
  },
  {
    value: 'CARRY_FORWARD',
    label: 'Entered once, carried forward',
    description: 'A retainer - type it once and it applies until the price changes.',
  },
  {
    value: 'DERIVED',
    label: 'Calculated (count × rate)',
    description: "The system counts the month's usage and multiplies by your rate.",
  },
] as const;

const DRIVER_OPTIONS = [
  { value: 'WHATSAPP_MESSAGES', label: 'WhatsApp messages sent', description: 'Rate per message' },
  { value: 'WHATSAPP_CALLS', label: 'WhatsApp calls connected', description: 'Rate per call' },
  { value: 'TBO_API_CALLS', label: 'TBO API calls', description: 'Rate per API call' },
] as const;

/** Sentinel for the "no channel" choice - Radix forbids an empty value. */
const OVERHEAD = '__overhead__';

const CostHeadFields = ({
  pending,
  isCreate,
  code,
}: {
  pending: boolean;
  isCreate: boolean;
  code: string;
}): React.JSX.Element => {
  const mode = useWatch<CreateMarketingCostHeadRequest, 'mode'>({ name: 'mode' });
  const sources = useQuerySourceOptions();
  const sourceOptions = [
    {
      value: OVERHEAD,
      label: 'All channels (overhead)',
      description: 'Counted once, in the Overall block only',
    },
    ...(sources.data ?? []).map((source) => ({ value: source.id, label: source.name })),
  ];

  return (
    <>
      <UzFormField name="name">
        {({ field, error }) => (
          <UzInput
            {...field}
            value={(field.value as string | undefined) ?? ''}
            label="Name"
            placeholder="e.g. WhatsApp, Google Ads, API cost"
            hint={isCreate ? undefined : `Code: ${code}`}
            error={error}
            required
            disabled={pending}
          />
        )}
      </UzFormField>

      <UzFormField name="mode">
        {({ field, error }) => (
          <UzSelect
            label="How is the monthly figure found?"
            value={field.value as string}
            onValueChange={field.onChange}
            options={MODE_OPTIONS}
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>

      {mode === 'DERIVED' && (
        <>
          <UzFormField name="driver">
            {({ field, error }) => (
              <UzSelect
                label="What to count"
                placeholder="Pick what the system counts"
                value={(field.value as string | null) ?? undefined}
                onValueChange={field.onChange}
                options={DRIVER_OPTIONS}
                error={error}
                required
                disabled={pending}
              />
            )}
          </UzFormField>
          <UzFormField name="rate">
            {({ field, error }) => (
              <UzNumberInput
                name={field.name}
                label="Rate (₹ per unit)"
                precision={4}
                value={
                  field.value === null || field.value === undefined ? '' : (field.value as number)
                }
                onValueChange={(v) => field.onChange(v ?? null)}
                error={error}
                required
                disabled={pending}
              />
            )}
          </UzFormField>
        </>
      )}

      <UzFormField name="querySourceId">
        {({ field, error }) => (
          <UzSelect
            label="Channel"
            hint="A cost tied to one lead source (e.g. a Google Ads account) sits in that channel's block on the sheet as well as in Overall."
            value={(field.value as string | null) ?? OVERHEAD}
            onValueChange={(value) => field.onChange(value === OVERHEAD ? null : value)}
            options={sourceOptions}
            error={error}
            disabled={pending || sources.isLoading}
          />
        )}
      </UzFormField>

      <UzFormField name="description">
        {({ field, error }) => (
          <UzTextarea
            {...field}
            value={(field.value as string | undefined) ?? ''}
            label="Description"
            placeholder="Optional"
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>

      <UzFormField name="isRequired">
        {({ field }) => (
          <UzSwitch
            label="Required"
            description="A month with no figure for this cost shows '-' on its profit rows until one is entered. Off: a missing month simply adds nothing."
            checked={Boolean(field.value)}
            onCheckedChange={field.onChange}
            disabled={pending}
          />
        )}
      </UzFormField>

      <UzFormField name="sortOrder">
        {({ field, error }) => (
          <UzNumberInput
            name={field.name}
            label="Sort order"
            precision={0}
            value={field.value === null || field.value === undefined ? '' : (field.value as number)}
            onValueChange={(v) => field.onChange(v ?? 0)}
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>

      <UzFormField name="isActive">
        {({ field }) => (
          <UzSwitch
            label="Active"
            description="Inactive heads stay out of the report and its cost picker; their figures are kept."
            checked={Boolean(field.value)}
            onCheckedChange={field.onChange}
            disabled={pending}
          />
        )}
      </UzFormField>
    </>
  );
};

/**
 * Create / edit one cost head. One form for both: the create payload is the
 * full row, and the update endpoint takes any subset of the same fields, so
 * edit simply sends everything but the code (the stable machine identifier,
 * read-only after create).
 */
export const CostHeadFormDrawer = ({
  open,
  onOpenChange,
  mode,
  head,
}: CostHeadFormDrawerProps): React.JSX.Element => {
  const isCreate = mode === 'create';
  const create = useCreateCostHead();
  const update = useUpdateCostHead();
  const pending = create.isPending || update.isPending;

  const form = useForm<CreateMarketingCostHeadRequest>({
    resolver: zodResolver(CreateMarketingCostHeadRequestSchema),
    defaultValues: CREATE_DEFAULTS,
  });

  // The code is derived from the name on create; the operator never types it.
  useAutoDeriveCode(form, isCreate);

  useEffect(() => {
    if (!open) return;
    if (isCreate || !head) {
      form.reset(CREATE_DEFAULTS);
      return;
    }
    form.reset({
      code: head.code,
      name: head.name,
      description: head.description ?? '',
      mode: head.mode,
      driver: head.driver,
      rate: head.rate,
      querySourceId: head.querySourceId,
      isRequired: head.isRequired,
      isActive: head.isActive,
      sortOrder: head.sortOrder,
    });
  }, [open, isCreate, head, form]);

  const onSubmit = form.handleSubmit(async (values) => {
    const derived = values.mode === 'DERIVED';
    const payload = {
      ...values,
      description: values.description?.trim() ? values.description : null,
      driver: derived ? values.driver : null,
      rate: derived ? values.rate : null,
    };
    if (isCreate) {
      await create.mutateAsync({ ...payload, code: deriveCode(values.name) });
    } else if (head) {
      const { code: _code, ...rest } = payload;
      await update.mutateAsync({ id: head.id, body: rest });
    }
    onOpenChange(false);
  });

  return (
    <UzDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={isCreate ? 'Add cost head' : `Edit ${head?.name ?? 'cost head'}`}
      description="One row of the Spend section on the Marketing report."
      footer={
        <div className="flex justify-end gap-2">
          <UzButton variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </UzButton>
          <UzButton onClick={onSubmit} loading={pending}>
            {isCreate ? 'Add' : 'Save changes'}
          </UzButton>
        </div>
      }
    >
      <UzForm form={form} onSubmit={onSubmit}>
        <CostHeadFields pending={pending} isCreate={isCreate} code={head?.code ?? ''} />
      </UzForm>
    </UzDrawer>
  );
};
